import React, { useState, useEffect, useCallback } from "react";
import leaveService from "../../services/leaveService";
import holidayService from "../../services/holidayService";
import { getErrorMessage } from "../../utils/errorHandler";
import Navbar from "../../components/layout/Navbar";
import Sidebar from "../../components/layout/Sidebar";
import { AlertTriangle, CheckCircle2, ShieldAlert, Calendar, FileText, ChevronLeft, Upload, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DAY_PART_OPTIONS, isHalfDay, requiresAttachment, getStatusLabel } from "../../utils/leaveDisplay";
import "../../styles/dashboard.css";

const toLocalISODate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const buildCalendarDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  // Calendrier commençant le lundi (format courant en Tunisie).
  const mondayOffset = (firstDay.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
};

const calendarNavButtonStyle = {
  width: 30,
  height: 30,
  border: "1px solid #dbe3ef",
  borderRadius: 7,
  background: "#fff",
  color: "#334155",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer",
};

const CreateLeaveRequest = () => {
  const navigate = useNavigate();

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState(null);

  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [dayPart, setDayPart] = useState("FULL_DAY");
  const [attachment, setAttachment] = useState(null);

  const [duration, setDuration] = useState(0);
  const [durationMessage, setDurationMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dateConflict, setDateConflict] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [periodHolidays, setPeriodHolidays] = useState([]);
  const [loadingPeriodHolidays, setLoadingPeriodHolidays] = useState(false);

  // Calendrier de sélection avec repérage visuel des jours fériés.
  const [datePickerTarget, setDatePickerTarget] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [calendarHolidaysByDate, setCalendarHolidaysByDate] = useState({});
  const [loadingCalendarHolidays, setLoadingCalendarHolidays] = useState(false);

  
  const remarkStyles = {
    error: {
      background: "#FEF2F2",
      border: "1px solid #FCA5A5",
      color: "#B91C1C",
      icon: "#DC2626",
    },
    warning: {
      background: "#FFF7ED",
      border: "1px solid #FDBA74",
      color: "#C2410C",
      icon: "#EA580C",
    },
    success: {
      background: "#ECFDF5",
      border: "1px solid #86EFAC",
      color: "#047857",
      icon: "#059669",
    },
  };


  useEffect(() => {
    const loadFormData = async () => {
      try {
        console.log("Loading leave form data...");
        
        const types = await leaveService.getLeaveTypes();
        console.log("Leave types loaded:", types);
        
        // Filter out inactive leave types (if backend supports is_active)
        const activeTypes = types.filter(type => type.is_active !== false);
        setLeaveTypes(activeTypes);
        
        if (activeTypes.length > 0) {
          setSelectedTypeId(activeTypes[0].id.toString());
        }

        const balanceData = await leaveService.getLeaveBalancesMe();
        console.log("Balance data loaded:", balanceData);
        
        setBalances(balanceData);
        const annualDetail = balanceData?.balances_by_type?.find(
  (item) =>
    String(item.code || "").trim().toUpperCase() === "ANNUEL"
);

console.log("=== DIAGNOSTIC SOLDE ANNUEL ===");
console.log("annual_balance :", balanceData?.annual_balance);
console.log("Détail annuel complet :", annualDetail);
console.log("N-1 :", annualDetail?.remaining_n1);
console.log("N :", annualDetail?.remaining_n);
console.log("Total :", annualDetail?.remaining);
        
      } catch (err) {
        console.error("Error loading request form data:", err);
        console.error("Error response:", err.response);
        setError(getErrorMessage(err));
        
        // Set default values to allow the form to still work
        if (!leaveTypes.length) {
          setLeaveTypes([{id: 1, name: "Congé annuel", code: "ANNUEL", description: "Congé annuel"}]);
          setSelectedTypeId("1");
        }
        if (!balances) {
          setBalances({
            annual_balance: 24, 
            sick_balance: 5, 
            recovery_balance: 0, 
            marriage_balance: 3, 
            newborn_balance: 7, 
            death_close_balance: 3, 
            death_family_balance: 2, 
            circumcision_balance: 1
          });
        }
      } finally {
        setLoading(false);
      }
    };
    loadFormData();
  }, []);

  const calendarYear = calendarMonth.getFullYear();

  // Charger les jours fériés de l'année affichée dans le calendrier.
  // GET /holidays?year=YYYY est la source unique des marqueurs.
  useEffect(() => {
    let cancelled = false;

const loadCalendarHolidays = async () => {
  setLoadingCalendarHolidays(true);

  console.group(`📅 [CALENDRIER CONGÉS] GET jours fériés ${calendarYear}`);

  try {
    // 1. Appel API
    const response = await holidayService.getHolidays(calendarYear);

    // 2. Extraire les données
    const records = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.holidays)
            ? response.holidays
            : [];

    // 3. Logs de la réponse API
    console.log("➡️ Année demandée :", calendarYear);
    console.log("📥 Réponse brute de l'API :", response);
    console.log("📦 Nombre de jours fériés récupérés :", records.length);

    console.table(
      records.map((holiday) => ({
        id: holiday.id,
        nom: holiday.name,
        date: holiday.date,
        durée: holiday.duration_days,
        statut: holiday.payment_status,
        type: holiday.holiday_type,
        actif: holiday.is_active,
      }))
    );

    // 4. Organiser les jours fériés par date
    const nextMap = {};

    records.forEach((holiday) => {
      if (holiday?.is_active === false) {
        console.warn("Jour férié ignoré (inactif) :", holiday);
        return;
      }

      const start = String(holiday?.date || "").slice(0, 10);

      if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) {
        console.warn("Jour férié ignoré (date invalide) :", holiday);
        return;
      }

      const durationValue = Number(holiday?.duration_days ?? 1);

      const durationDays = Number.isFinite(durationValue)
        ? Math.max(1, Math.trunc(durationValue))
        : 1;

      const paymentStatus =
        String(holiday?.payment_status || "PAID")
          .trim()
          .toUpperCase() === "UNPAID"
          ? "UNPAID"
          : "PAID";

      for (let offset = 0; offset < durationDays; offset += 1) {
        const current = new Date(`${start}T00:00:00`);
        current.setDate(current.getDate() + offset);

        const dateKey = toLocalISODate(current);

        if (!dateKey.startsWith(`${calendarYear}-`)) continue;

        if (!nextMap[dateKey]) {
          nextMap[dateKey] = [];
        }

        nextMap[dateKey].push({
          id: holiday.id,
          name: holiday.name || "Jour férié",
          payment_status: paymentStatus,
          holiday_type: holiday.holiday_type || "NATIONAL",
        });
      }
    });

    // 5. Afficher les jours fériés organisés par date
    console.log("🗓️ Jours fériés organisés par date :", nextMap);

    console.table(
      Object.entries(nextMap).flatMap(([date, holidays]) =>
        holidays.map((holiday) => ({
          date,
          nom: holiday.name,
          statut: holiday.payment_status,
          type: holiday.holiday_type,
        }))
      )
    );

    // 6. Mettre à jour le calendrier
    if (!cancelled) {
      setCalendarHolidaysByDate((previous) => ({
        ...previous,
        [calendarYear]: nextMap,
      }));

      console.log("✅ Jours fériés envoyés au calendrier :", nextMap);
    }

  } catch (err) {
    console.error(
      `[CreateLeaveRequest] Erreur chargement jours fériés ${calendarYear}:`,
      err?.response?.data || err
    );

    if (!cancelled) {
      setCalendarHolidaysByDate((previous) => ({
        ...previous,
        [calendarYear]: {},
      }));
    }

  } finally {
    if (!cancelled) {
      setLoadingCalendarHolidays(false);
    }

    console.groupEnd();
  }
};

    // IMPORTANT : lancer réellement le chargement des jours fériés
    loadCalendarHolidays();

    return () => {
      cancelled = true;
    };
  }, [calendarYear]);

  const openDatePicker = (target, selectedValue) => {
    const baseDate = selectedValue
      ? new Date(`${selectedValue}T00:00:00`)
      : new Date();
    setCalendarMonth(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
    setDatePickerTarget((current) => current === target ? null : target);
  };

  const moveCalendarMonth = (offset) => {
    setCalendarMonth((current) =>
      new Date(current.getFullYear(), current.getMonth() + offset, 1)
    );
  };

  const renderDatePicker = (target, label, value, disabled = false) => {
    const displayedDate = value
      ? new Date(`${value}T00:00:00`).toLocaleDateString("fr-FR")
      : "Choisir une date";
    const days = buildCalendarDays(calendarMonth);
    const monthKey = calendarYear;
    const holidayMap = calendarHolidaysByDate[monthKey] || {};
    const weekDays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

    return (
      <div className="form-group" style={{ position: "relative", minWidth: 0 }}>
        <label className="form-label">{label}</label>
        <button
          type="button"
          className="form-input"
          disabled={disabled}
          onClick={() => openDatePicker(target, value)}
          aria-haspopup="dialog"
          aria-expanded={datePickerTarget === target}
          style={{
            width: "100%",
            minHeight: 42,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "left",
            background: disabled ? "#f1f5f9" : "#fff",
            color: value ? "#0f172a" : "#64748b",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <span>{displayedDate}</span>
          <Calendar size={16} color="#64748b" />
        </button>

        {datePickerTarget === target && !disabled && (
          <div
            role="dialog"
            aria-label={`Calendrier - ${label}`}
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: target === "end" ? "auto" : 0,
              right: target === "end" ? 0 : "auto",
              zIndex: 1000,
              width: "min(330px, calc(100vw - 32px))",
              maxWidth: "calc(100vw - 32px)",
              boxSizing: "border-box",
              padding: 14,
              border: "1px solid #dbe3ef",
              borderRadius: 12,
              background: "#fff",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.16)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <button type="button" onClick={() => moveCalendarMonth(-1)} aria-label="Mois précédent" style={calendarNavButtonStyle}>‹</button>
              <strong style={{ textTransform: "capitalize", color: "#1e293b" }}>
                {calendarMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
              </strong>
              <button type="button" onClick={() => moveCalendarMonth(1)} aria-label="Mois suivant" style={calendarNavButtonStyle}>›</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 5 }}>
              {weekDays.map((day) => (
                <div key={day} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "#64748b", padding: "4px 0" }}>{day}</div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
              {days.map((day) => {
                const dateKey = toLocalISODate(day);
                const inCurrentMonth = day.getMonth() === calendarMonth.getMonth();
// Jours fériés récupérés depuis le calendrier annuel
const holidaysFromCalendar = holidayMap[dateKey] || [];

// Jours fériés récupérés depuis la période sélectionnée
const holidaysFromPeriod = periodHolidays
  .filter((holiday) => holiday.date === dateKey);

// Fusionner les deux sources
const holidayList = Array.from(
  new Map(
    [...holidaysFromCalendar, ...holidaysFromPeriod].map((holiday) => [
      `${holiday.id}-${holiday.name}-${holiday.payment_status}`,
      holiday,
    ])
  ).values()
);

// Déterminer le statut du jour férié
const isPaidHoliday = holidayList.some(
  (item) => item.payment_status === "PAID"
);

const isUnpaidHoliday =
  !isPaidHoliday &&
  holidayList.some((item) => item.payment_status === "UNPAID");

const isHoliday = isPaidHoliday || isUnpaidHoliday;
                const isSelected = value === dateKey;

                const title = holidayList.length
                  ? holidayList.map((item) => `${item.name} — ${item.payment_status === "UNPAID" ? "chômé non payé" : "chômé payé"}`).join("\n")
                  : dateKey;

                return (
                  <button
                    key={dateKey}
                    type="button"
                    title={title}
                    aria-label={isHoliday ? `${dateKey}, ${title}` : dateKey}
                    onClick={() => {
                      if (target === "start") {
                        handleStartDateChange(dateKey);
                      } else {
                        setEndDate(dateKey);
                      }
                      setDatePickerTarget(null);
                    }}
                    style={{
                      position: "relative",
                      minHeight: 36,
                      padding: "5px 2px 8px",
                      borderRadius: 8,
                      border: isSelected ? "2px solid #2563eb" : "1px solid transparent",
                      background: isSelected
                        ? "#dbeafe"
                        : isPaidHoliday
                          ? "#dcfce7"
                          : isUnpaidHoliday
                            ? "#fee2e2"
                            : "#fff",
                      color: !inCurrentMonth ? "#cbd5e1" : isPaidHoliday ? "#166534" : isUnpaidHoliday ? "#991b1b" : "#334155",
                      fontWeight: isHoliday || isSelected ? 700 : 500,
                      cursor: inCurrentMonth ? "pointer" : "default",
                      opacity: inCurrentMonth ? 1 : 0.45,
                    }}
                    disabled={!inCurrentMonth}
                  >
                    {day.getDate()}
                    {isHoliday && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          bottom: 3,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: isPaidHoliday ? "#16a34a" : "#dc2626",
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12, paddingTop: 10, borderTop: "1px solid #e2e8f0", fontSize: 11, color: "#475569" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a" }} />Chômé payé</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626" }} />Chômé non payé</span>
              {loadingCalendarHolidays && <span style={{ color: "#64748b" }}>Chargement...</span>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const calculateDuration = useCallback(async () => {
    if (!startDate || !endDate) {
      setDuration(0);
      setDurationMessage("");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setDuration(0);
      setDurationMessage("");
      return;
    }

    if (isHalfDay(dayPart) && startDate !== endDate) {
      setDuration(0);
      setDurationMessage(
        "Pour une demi-journée, la date de fin doit être identique à la date de début."
      );
      return;
    }

    setCalculating(true);
    try {
      const result = await leaveService.calculateLeaveDays(startDate, endDate, dayPart);
      setDuration(result.duration_days);
      setDurationMessage(result.message);
      setError("");
    } catch (err) {
      setError(getErrorMessage(err));
      setDuration(0);
      setDurationMessage("");
    } finally {
      setCalculating(false);
    }
  }, [startDate, endDate, dayPart]);

  useEffect(() => {
    calculateDuration();
  }, [calculateDuration]);

  useEffect(() => {
    let cancelled = false;

    const loadHolidaysForPeriod = async () => {
      if (!startDate || !endDate || startDate > endDate) {
        setPeriodHolidays([]);
        setLoadingPeriodHolidays(false);
        return;
      }

      setLoadingPeriodHolidays(true);

      try {
        // Charger toutes les années couvertes par la période demandée.
        const firstYear = Number(startDate.slice(0, 4));
        const lastYear = Number(endDate.slice(0, 4));
        const years = Array.from(
          { length: lastYear - firstYear + 1 },
          (_, index) => firstYear + index
        );

        const responses = await Promise.all(
          years.map((year) => holidayService.getHolidays(year))
        );

        const allHolidays = responses.flatMap((response) => {
          if (Array.isArray(response)) return response;
          if (Array.isArray(response?.data)) return response.data;
          if (Array.isArray(response?.items)) return response.items;
          return [];
        });

        const visibleDates = [];

        allHolidays.forEach((holiday) => {
          if (holiday?.is_active === false) return;

          const holidayStart = String(holiday?.date || "").slice(0, 10);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(holidayStart)) return;

          const rawDuration = Number(holiday?.duration_days || 1);
          const holidayDuration = Number.isFinite(rawDuration)
            ? Math.max(1, Math.trunc(rawDuration))
            : 1;
          const paymentStatus = String(
            holiday?.payment_status || "PAID"
          ).trim().toUpperCase();

          for (let offset = 0; offset < holidayDuration; offset += 1) {
            const current = new Date(`${holidayStart}T00:00:00`);
            current.setDate(current.getDate() + offset);
            const currentDate = [
              current.getFullYear(),
              String(current.getMonth() + 1).padStart(2, "0"),
              String(current.getDate()).padStart(2, "0"),
            ].join("-");

            if (currentDate >= startDate && currentDate <= endDate) {
              visibleDates.push({
                id: `${holiday.id || holiday.name}-${currentDate}`,
                name: holiday.name || "Jour férié",
                date: currentDate,
                payment_status: paymentStatus === "UNPAID" ? "UNPAID" : "PAID",
                holiday_type: holiday.holiday_type || "NATIONAL",
              });
            }
          }
        });

        if (!cancelled) {
          // Une seule ligne par date et par jour férié.
          const unique = Array.from(
            new Map(
              visibleDates.map((item) => [`${item.id}`, item])
            ).values()
          ).sort((a, b) => a.date.localeCompare(b.date));
          setPeriodHolidays(unique);
        }
      } catch (err) {
        console.error(
          "[CreateLeaveRequest] Impossible de charger les jours fériés de la période :",
          err
        );
        if (!cancelled) setPeriodHolidays([]);
      } finally {
        if (!cancelled) setLoadingPeriodHolidays(false);
      }
    };

    loadHolidaysForPeriod();

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const handleDayPartChange = (value) => {
    setDayPart(value);
    if (isHalfDay(value) && startDate) {
      setEndDate(startDate);
    }
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (isHalfDay(dayPart) && value) {
      setEndDate(value);
    }
  };

  const getSelectedTypeDetails = () => {
    return leaveTypes.find(t => t.id.toString() === selectedTypeId);
  };

  const getSelectedTypeBalance = () => {
    const type = getSelectedTypeDetails();

    if (!type || !balances) return 0;

    // Congé sans solde : illimité
    if (type.code === "SANS_SOLDE") {
      return "Illimité";
    }

    const balanceDetail = balances.balances_by_type?.find(
      (item) =>
        String(item.code || "").trim().toUpperCase() ===
        String(type.code || "").trim().toUpperCase()
    );

    if (!balanceDetail) return 0;

    // Pour le congé annuel, additionner N-1 et N.
    if (type.code === "ANNUEL") {
      const previousYearRemaining = Number(
        balanceDetail.remaining_n1 ??
        balanceDetail.previous_year_remaining ??
        balanceDetail.n1_remaining ??
        0
      );

      const currentYearRemaining = Number(
        balanceDetail.remaining_n ??
        balanceDetail.current_year_remaining ??
        balanceDetail.n_remaining ??
        balanceDetail.remaining ??
        0
      );

      return Math.max(0, previousYearRemaining) +
            Math.max(0, currentYearRemaining);
    }

    return Number(balanceDetail.remaining ?? 0);
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError("Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG");
        return;
      }
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("La taille du fichier ne doit pas dépasser 5 Mo");
        return;
      }
      setAttachment(file);
      setError("");
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  
const checkDateConflict = async (newStartDate, newEndDate) => {
  const response = await leaveService.getLeaveRequestsMe();

  // Accepter une réponse API sous forme de tableau
  // ou sous forme d'objet contenant les demandes.
  const requests = Array.isArray(response)
    ? response
    : response?.items ||
      response?.requests ||
      response?.leave_requests ||
      [];

  const ignoredStatuses = [
    "REJECTED",
    "REFUSED",
    "CANCELLED",
    "CANCELED",
    "ANNULEE",
    "ANNULÉE",
    "REFUSEE",
    "REFUSÉE",
  ];

  const start = new Date(`${newStartDate}T00:00:00`);
  const end = new Date(`${newEndDate}T00:00:00`);

  return requests.find((request) => {
    const status = String(
      request.status || request.statut || ""
    )
      .trim()
      .toUpperCase();

    // Les demandes refusées ou annulées ne bloquent pas les dates.
    if (ignoredStatuses.includes(status)) {
      return false;
    }

    const existingStartValue =
      request.start_date || request.date_debut;

    const existingEndValue =
      request.end_date || request.date_fin || existingStartValue;

    if (!existingStartValue || !existingEndValue) {
      return false;
    }

    const existingStart = new Date(
      `${String(existingStartValue).slice(0, 10)}T00:00:00`
    );

    const existingEnd = new Date(
      `${String(existingEndValue).slice(0, 10)}T00:00:00`
    );

    // Deux périodes se chevauchent si elles ont au moins
    // une journée en commun.
    return start <= existingEnd && end >= existingStart;
  });
};


useEffect(() => {
  let cancelled = false;

  const verifyDates = async () => {
    setDateConflict(null);

    if (!startDate || !endDate || startDate > endDate) {
      setCheckingConflict(false);
      return;
    }

    if (isHalfDay(dayPart) && startDate !== endDate) {
      setCheckingConflict(false);
      return;
    }

    setCheckingConflict(true);

    try {
      const conflict = await checkDateConflict(startDate, endDate);

      if (!cancelled) {
        setDateConflict(conflict || null);
      }
    } catch (err) {
      if (!cancelled) {
        // En cas d'échec du contrôle, ne pas autoriser
        // la soumission sans vérification.
        setDateConflict({ checkFailed: true });
      }
    } finally {
      if (!cancelled) {
        setCheckingConflict(false);
      }
    }
  };

  verifyDates();

  return () => {
    cancelled = true;
  };
}, [startDate, endDate, dayPart]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (new Date(startDate) > new Date(endDate)) {
      setError("La date de fin doit être supérieure ou égale à la date de début.");
      return;
    }

    if (isHalfDay(dayPart) && startDate !== endDate) {
      setError(
        "Une demi-journée ne peut être demandée que sur une seule date (date de début = date de fin)."
      );
      return;
    }

    const currentBalance = getSelectedTypeBalance();
    // Skip balance check for unpaid leave (SANS_SOLDE)
    if (getSelectedTypeDetails()?.code !== "SANS_SOLDE" && duration > currentBalance) {
      setError(
        `Solde insuffisant pour « ${getSelectedTypeDetails()?.name} ». ` +
        `Disponible : ${currentBalance} j, demandé : ${duration} j.`
      );
      return;
    }

    if (duration === 0) {
      setError("La durée calculée est de 0 jour. Vérifiez que votre demande ne contient pas uniquement des week-ends ou des jours fériés.");
      return;
    }

    setSubmitting(true);
    try {
  const conflict = await checkDateConflict(
    startDate,
    endDate
  );

  if (conflict) {
    const conflictType =
      conflict.leave_type?.name ||
      conflict.leave_type_name ||
      conflict.type_conge ||
      conflict.leave_type ||
      "une autre demande";

    setError(
      `Impossible de soumettre cette demande : ` +
      `une demande (${conflictType}) existe déjà sur ` +
      `cette période, du ${
        String(conflict.start_date || conflict.date_debut).slice(0, 10)
      } au ${
        String(
          conflict.end_date ||
          conflict.date_fin ||
          conflict.start_date ||
          conflict.date_debut
        ).slice(0, 10)
      }. Choisissez d'autres dates.`
    );

    return;
  }

  const payload = {
    leave_type_id: Number(selectedTypeId),
    start_date: startDate,
    end_date: endDate,
    day_part: dayPart,
    reason: reason.trim(),
  };

  await leaveService.createLeaveRequest(payload, attachment);

  setSuccess("Votre demande a été soumise avec succès !");
  setStartDate("");
  setEndDate("");
  setReason("");
  setAttachment(null);

  setTimeout(() => {
    navigate("/employee/leave-history");
  }, 1500);
} catch (err) {
  setError(getErrorMessage(err));
} finally {
  setSubmitting(false);
}
 }; // Ferme handleSubmit

  const selectedType = getSelectedTypeDetails();
  const selectedBalance = getSelectedTypeBalance();
  const insufficientBalance =
    selectedType?.code !== "SANS_SOLDE" && duration > 0 && duration > selectedBalance;
  const halfDayDateMismatch =
    isHalfDay(dayPart) && startDate && endDate && startDate !== endDate;
  const invalidDates =
    startDate && endDate && new Date(startDate) > new Date(endDate);
  const canSubmit =
    !loading &&
    !submitting &&
    !calculating &&
    !checkingConflict &&
    !dateConflict &&
    !error &&
    !!selectedTypeId &&
    !!startDate &&
    !!endDate &&
    !invalidDates &&
    !halfDayDateMismatch &&
    !insufficientBalance &&
    duration > 0;


  if (loading) {
    return (
      <div className="flex-center-all" style={{ height: "100vh", background: "#f8fafc", flexDirection: "column" }}>
        <div className="spinner" style={{ width: "40px", height: "40px", border: "4px solid #e2e8f0", borderTopColor: "#10b981", borderRadius: "50%" }}></div>
        <p style={{ marginTop: "15px", color: "#64748b", fontFamily: "sans-serif" }}>Initialisation du formulaire...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Supprimer le scroll interne à droite du formulaire.
           Le défilement doit se faire sur la page, pas dans le panneau. */
        .dashboard-content-wrapper,
        .leave-request-main,
        .leave-request-card,
        .leave-request-card form {
          min-width: 0;
          max-width: 100%;
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }
        .leave-request-main {
          flex: 1 1 auto;
        }
        .leave-request-card {
          overflow: visible !important;
        }
        html,
        body {
          overflow-x: hidden;
        }
        .leave-request-card .form-group,
        .leave-request-card .form-group-row > * {
          min-width: 0;
        }
        .leave-request-card input,
        .leave-request-card select,
        .leave-request-card textarea,
        .leave-request-card button {
          max-width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 640px) {
          .leave-request-card {
            padding: 18px !important;
          }
          .leave-date-row {
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 12px !important;
          }
          .leave-request-card .day-part-options {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
      <div className="dashboard-layout-container">
      <Navbar />

      <div className="dashboard-content-wrapper">
        <Sidebar />

        <main className="dashboard-main-content leave-request-main" style={{ minWidth: 0, width: "100%", boxSizing: "border-box", overflow: "visible", padding: "clamp(14px, 2.5vw, 28px)" }}>
          <button onClick={() => navigate(-1)} className="btn btn-outline btn-icon-group" style={{ marginBottom: "20px", display: "inline-flex", alignItems: "center" }}>
            <ChevronLeft size={16} />
            <span>Retour</span>
          </button>

          <div className="card-inner-box leave-request-card" style={{ width: "100%", maxWidth: "760px", minWidth: 0, margin: "0 auto", padding: "clamp(18px, 3vw, 32px)", boxSizing: "border-box", overflow: "visible" }}>
            <div className="card-box-header" style={{ marginBottom: "25px" }}>
              <h2 style={{ fontSize: "1.8rem", color: "#1e293b", fontWeight: "600" }}>Nouvelle demande d'absence</h2>
              <p>Remplissez les détails ci-dessous pour planifier vos congés.</p>
            </div>

            {success && (
              <div className="flash-success-toast" style={{ marginBottom: "20px", position: "relative", transform: "none", top: "auto", left: "auto" }}>
                <CheckCircle2 size={20} className="toast-icon" />
                <span>{success}</span>
              </div>
            )}

          
            {error && (
              <div
                role="alert"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "14px 16px",
                  marginBottom: "20px",
                  borderRadius: "8px",
                  ...remarkStyles.error,
                  fontSize: "13px",
                  fontWeight: 600,
                  lineHeight: 1.6,
                }}
              >
                <ShieldAlert
                  size={20}
                  color={remarkStyles.error.icon}
                  style={{ flexShrink: 0 }}
                />
                <span>{error}</span>
              </div>
            )}
 
            {checkingConflict && (
              <div
                role="status"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "12px 15px",
                  marginBottom: "20px",
                  borderRadius: "8px",
                  ...remarkStyles.warning,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                <AlertTriangle size={18} />
                Vérification des dates et des demandes existantes...
              </div>
            )}

            {dateConflict && !dateConflict.checkFailed && (
              <div
                role="alert"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "14px 16px",
                  marginBottom: "20px",
                  borderRadius: "8px",
                  ...remarkStyles.error,
                  fontSize: "13px",
                  lineHeight: 1.6,
                }}
              >
                <ShieldAlert size={20} style={{ flexShrink: 0 }} />
                <span>
                  <strong>Dates indisponibles.</strong>
                  {" "}Une autre demande de congé existe déjà sur cette période.
                  Choisissez une autre date.
                </span>
              </div>
            )}

            {dateConflict?.checkFailed && (
              <div
                role="alert"
                style={{
                  padding: "12px 15px",
                  marginBottom: "20px",
                  borderRadius: "8px",
                  ...remarkStyles.warning,
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                <AlertTriangle size={18} style={{ marginRight: 8, verticalAlign: "middle" }} />
                Impossible de vérifier les demandes existantes. Réessayez avant de soumettre.
              </div>
            )}


            <form onSubmit={handleSubmit} className="drawer-form" style={{ padding: "0" }}>
              <div className="form-group">
                <label className="form-label">Type de congé</label>
                <select
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                  className="form-select"
                  required
                  disabled={leaveTypes.length === 0}
                >
                  {leaveTypes.map((type) => (
                    <option key={type.id} value={type.id.toString()}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {selectedType && (
                  <span className="input-help-text" style={{ display: "block", marginTop: "5px", color: "#64748b" }}>
                    {selectedType.description} | <strong>Solde disponible : {selectedBalance} jours</strong>
                    {selectedType.code === "SANS_SOLDE" && (
                      <span style={{ display: "block", marginTop: "3px", color: "#10b981", fontSize: "13px" }}>
                        ✓ Ce type de congé n'est pas déduit de votre solde annuel.
                      </span>
                    )}
                  </span>
                )}
              </div>

              {/* ==========================================
    DURÉE DE LA DEMANDE
    ========================================== */}
<div className="form-group">
  <label className="form-label">Durée de la demande</label>

  <div
    className="day-part-options"
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px",
      marginTop: "10px",
    }}
  >
    {DAY_PART_OPTIONS.map((option) => {
      const isSelected = dayPart === option.value;

      return (
        <label
          key={option.value}
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "90px",
            padding: "15px 10px",
            borderRadius: "10px",
            border: isSelected
              ? "2px solid #10b981"
              : "1px solid #dbe3ef",
background: isSelected ? "#ecfdf5" : "#ffffff",
            cursor: "pointer",
            transition: "all 0.2s ease",
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          <input
            type="radio"
            name="day_part"
            value={option.value}
            checked={isSelected}
            onChange={() => handleDayPartChange(option.value)}
            style={{
              width: "20px",
              height: "20px",
              margin: "0 0 8px 0",
              accentColor: "#10b981",
              cursor: "pointer",
            }}
          />

          <span
            style={{
              fontSize: "14px",
              fontWeight: isSelected ? "600" : "500",
              color: isSelected ? "#047857" : "#334155",
              lineHeight: "1.3",
            }}
          >
            {option.label}
          </span>
        </label>
      );
    })}
  </div>
</div>

              <div className="form-group-row leave-date-row" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", alignItems: "start", width: "100%", minWidth: 0 }}>
                {renderDatePicker("start", "Date de début", startDate)}
                {renderDatePicker(
                  "end",
                  "Date de fin",
                  endDate,
                  isHalfDay(dayPart)
                )}
              </div>

              {startDate && endDate && !invalidDates && (
                <div
                  style={{
                    margin: "12px 0 15px",
                    padding: "12px 14px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontWeight: 600, color: "#334155", marginBottom: 8 }}>
                    Jours fériés dans la période
                  </div>

                  {loadingPeriodHolidays ? (
                    <span style={{ color: "#64748b", fontSize: 13 }}>
                      Vérification des jours fériés...
                    </span>
                  ) : periodHolidays.length > 0 ? (
                    <div style={{ display: "grid", gap: 7 }}>
                      {periodHolidays.map((holiday) => (
                        <div
                          key={holiday.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                            fontSize: 13,
                          }}
                        >
                          <span style={{ color: "#334155" }}>
                            <strong>{new Date(`${holiday.date}T00:00:00`).toLocaleDateString("fr-FR")}</strong>
                            {" — "}{holiday.name}
                          </span>
                          <span
                            style={{
                              padding: "3px 9px",
                              borderRadius: 20,
                              fontWeight: 600,
                              color: holiday.payment_status === "UNPAID" ? "#991b1b" : "#166534",
                              background: holiday.payment_status === "UNPAID" ? "#fee2e2" : "#dcfce7",
                            }}
                          >
                            {holiday.payment_status === "UNPAID" ? "Chômé non payé · décompté" : "Chômé payé · non décompté"}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: "#64748b", fontSize: 13 }}>
                      Aucun jour férié enregistré dans cette période.
                    </span>
                  )}

                  <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>
                    Les jours fériés payés sont exclus du calcul. Les jours fériés non payés en semaine sont comptés comme jours de congé.
                  </div>
                </div>
              )}

              {halfDayDateMismatch && (
                <div className="form-error-box" style={{ margin: "10px 0" }}>
                  <AlertTriangle size={16} />
                  <span>
                    Une demi-journée ne peut être demandée que sur une seule date.
                  </span>
                </div>
              )}

              {invalidDates && (
                <div className="form-error-box" style={{ margin: "10px 0" }}>
                  <AlertTriangle size={16} />
                  <span>La date de fin doit être supérieure ou égale à la date de début.</span>
                </div>
              )}

              {!invalidDates && duration > 0 && (
                <div className="days-calc-info" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", padding: "12px 15px", borderRadius: "6px", display: "flex", alignItems: "center", gap: "10px", margin: "15px 0" }}>
                  <Calendar size={18} />
                  <span>
                    Durée calculée : <strong>{duration === 0.5 ? "0,5 jour ouvrable" : `${duration} jours ouvrables`}</strong>
                    {durationMessage && ` — ${durationMessage}`}
                  </span>
                </div>
              )}

              {insufficientBalance && (
                <div className="form-error-box" style={{ margin: "10px 0" }}>
                  <AlertTriangle size={16} />
                  <span>
                    Solde insuffisant. Disponible : {selectedBalance} j, demandé : {duration} j.
                  </span>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Motif de la demande</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Expliquez brièvement la raison de votre congé (optionnel)..."
                  className="form-textarea"
                  rows={4}
                />
              </div>

              {selectedType && requiresAttachment(selectedType.code) && (
                <div className="form-group">
                  <label className="form-label">
                    Pièce jointe <span style={{ fontWeight: 'normal', color: '#64748b' }}>(Facultatif)</span>
                  </label>
                  <div style={{ border: '1px dashed #cbd5e1', borderRadius: '6px', padding: '20px', textAlign: 'center', background: '#f8fafc' }}>
                    {attachment ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '10px 15px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <FileText size={18} style={{ color: '#10b981' }} />
                          <span style={{ fontSize: '14px' }}>{attachment.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeAttachment}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          id="attachment"
                          onChange={handleAttachmentChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                        />
                        <label
                          htmlFor="attachment"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            background: '#10b981',
                            color: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <Upload size={16} />
                          Choisir un fichier
                        </label>
                        <p style={{ marginTop: '10px', fontSize: '12px', color: '#64748b' }}>
                          Formats acceptés : PDF, JPG, JPEG, PNG (max 5 Mo)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="drawer-footer-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "30px", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
                <button
                  type="button"
                  onClick={() => navigate("/employee/dashboard")}
                  className="btn btn-outline"
                  disabled={submitting}
                >
                  Annuler
                </button>
               
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn btn-primary btn-icon-group"
                  style={{
                    background: canSubmit ? "#10b981" : "#94a3b8",
                    border: "none",
                    cursor: canSubmit ? "pointer" : "not-allowed",
                    opacity: canSubmit ? 1 : 0.65,
                  }}
                >
                  <FileText size={18} />
                  <span>
                    {submitting
                      ? "Envoi en cours..."
                      : checkingConflict
                        ? "Vérification des dates..."
                        : dateConflict
                          ? "Dates déjà utilisées"
                          : calculating
                            ? "Calcul..."
                            : "Soumettre la demande"}
                  </span>
                </button>

              </div>
            </form>
          </div>
        </main>
      </div>
      </div>
    </>
  );
};

export default CreateLeaveRequest;
